import React, { useState, useEffect, useCallback, useRef } from 'react';
import config from '../../config';
import PlacesAutocompleteInput from '../../components/PlacesAutocompleteInput';
import './OpsRegister.css';

const API = config.opsApiBase.replace('/api/v1/ops', '');

const normalizePhone = (p) => {
  let c = p.replace(/[\s\-()]/g, '').replace(/^[+0]+/, '');
  return c.startsWith('91') ? c : c.length === 10 ? '91' + c : c;
};

const compactReg = (v) => (v || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();

const compressImage = (file, maxWidth = 1024, maxHeight = 1024, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => resolve(file);
    };
    reader.onerror = () => resolve(file);
  });
};

const parsePhoneExtensions = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((x) => String(x).trim()).filter(Boolean);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map((x) => String(x).trim()).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const getRegTonAndLength = (row) => {
  if (!row) return { ton: null, len: null };
  let ton = row.tonnage != null && row.tonnage !== '' ? String(row.tonnage) : (row.registration_specs?.tonnage != null && row.registration_specs?.tonnage !== '' ? String(row.registration_specs.tonnage) : null);
  let len = row.bed_length != null && row.bed_length !== '' ? String(row.bed_length) : (row.registration_specs?.bed_length != null && row.registration_specs?.bed_length !== '' ? String(row.registration_specs.bed_length) : null);
  if (!ton && !len && row.comments) {
    const tonMatch = String(row.comments).match(/(\d+(?:\.\d+)?\s*(?:ton|tons|t|kg))/i);
    const lenMatch = String(row.comments).match(/(\d+(?:\.\d+)?\s*(?:ft|feet|m\b))/i);
    if (tonMatch) ton = tonMatch[1];
    if (lenMatch) len = lenMatch[1];
  }
  return { ton, len };
};

const S = {
  page: {
    minHeight: '100vh',
    background: '#f4f6f9',
    fontFamily: "'Inter', sans-serif",
  },
  header: {
    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 100%)',
    color: '#fff',
    padding: '24px 20px 20px',
  },
  headerTitle: { fontSize: 22, fontWeight: 700, margin: 0 },
  headerSub: { fontSize: 13, opacity: 0.75, marginTop: 4, marginBottom: 0 },
  body: { padding: '20px 16px' },
  card: {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    padding: 22,
    marginBottom: 16,
  },
  label: { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5, display: 'block' },
  input: {
    width: '100%',
    padding: '11px 13px',
    border: '1px solid #d1d5db',
    borderRadius: 9,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    color: '#111',
    background: '#fff',
  },
  inputErr: {
    width: '100%',
    padding: '11px 13px',
    border: '1px solid #ef4444',
    borderRadius: 9,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    color: '#111',
    background: '#fff',
  },
  fg: { marginBottom: 16 },
  err: { color: '#ef4444', fontSize: 12, marginTop: 4 },
  hint: { color: '#9ca3af', fontSize: 12, marginTop: 4 },
  btn: {
    width: '100%',
    padding: '14px',
    borderRadius: 10,
    border: 'none',
    background: '#2563eb',
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 4,
  },
  btnDisabled: {
    width: '100%',
    padding: '14px',
    borderRadius: 10,
    border: 'none',
    background: '#93c5fd',
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'not-allowed',
    marginTop: 4,
  },
  alert: (c) => ({
    padding: '13px 16px',
    borderRadius: 9,
    marginBottom: 18,
    fontSize: 14,
    background: c === 'green' ? '#f0fdf4' : '#fef2f2',
    color: c === 'green' ? '#166534' : '#991b1b',
    border: `1px solid ${c === 'green' ? '#bbf7d0' : '#fecaca'}`,
  }),
  infoBox: {
    padding: '12px 14px',
    borderRadius: 9,
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    fontSize: 13,
    color: '#1e40af',
    marginBottom: 20,
  },
  tableCard: {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    padding: '18px 0 4px',
    marginBottom: 16,
    overflowX: 'auto',
  },
  tableTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1e3a5f',
    padding: '0 18px 12px',
    borderBottom: '1px solid #f3f4f6',
    marginBottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  th: {
    padding: '9px 12px',
    fontSize: 11,
    fontWeight: 700,
    color: '#6b7280',
    textAlign: 'left',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  td: {
    padding: '10px 12px',
    fontSize: 13,
    color: '#111',
    borderBottom: '1px solid #f3f4f6',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  stepBadge: (step) => ({
    display: 'inline-block',
    padding: '3px 9px',
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 700,
    background: step === 'completion' ? '#dcfce7' : '#fef3c7',
    color: step === 'completion' ? '#15803d' : '#92400e',
    textTransform: 'capitalize',
  }),
  refreshBtn: {
    fontSize: 12,
    padding: '4px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    background: '#fff',
    cursor: 'pointer',
    color: '#374151',
  },
};

export default function OpsRegister() {
  const [form, setForm] = useState({
    driverName: '',
    phoneNumber: '',
    vehicle: '',
    make: '',
    model: '',
    locationText: '',
    locationData: null,
    comments: '',
    bodyType: '',
    tonnage: '',
    bedLength: '',
    perKmPrice: '',
    readyForLongTrip: false,
    longInterDistrict: false,
    longInterState: false,
    local: false,
    intraStatePermit: false,
    phoneExtensions: [],
    registrationNo: '',
    vehicleClass: null,
  });
  const [newPhoneExt, setNewPhoneExt] = useState('');
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'green'|'red', text }

  // ── Vehicle photo state ───────────────────────────────────────────
  const [vehiclePhoto, setVehiclePhoto] = useState(null);       // File object
  const [vehiclePhotoPreview, setVehiclePhotoPreview] = useState(null); // data URL
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);         // full-screen preview

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const photoPickerRef = useRef(null);

  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editExt, setEditExt] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editPhoto, setEditPhoto] = useState(null);
  const editPhotoInputRef = useRef(null);

  const [regSuffix, setRegSuffix] = useState('');
  const [regSearchBusy, setRegSearchBusy] = useState(false);
  const [regSearch, setRegSearch] = useState(null); // { onboarded, vehicles, suffix } | { error }
  const [vehicleSuggestions, setVehicleSuggestions] = useState([]);
  const [vehicleSearchBusy, setVehicleSearchBusy] = useState(false);
  const [vehicleSuggestionsOpen, setVehicleSuggestionsOpen] = useState(false);

  const setEdit = (k, v) => setEditForm((f) => (f ? { ...f, [k]: v } : f));

  const fetchDrivers = useCallback(async ({ resetPage = true } = {}) => {
    setDriversLoading(true);
    try {
      const res = await fetch(`${config.opsApiBase}/driver-registration/list?status=all`);
      const data = await res.json();
      const list = data.records || [];
      const registered = list.filter(r => r.is_registered);
      registered.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
      setDrivers(registered);
      if (resetPage) setCurrentPage(1);
    } catch {
      setDrivers([]);
    } finally {
      setDriversLoading(false);
    }
  }, []);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  // Close photo picker on outside click
  useEffect(() => {
    const handle = (e) => {
      if (photoPickerRef.current && !photoPickerRef.current.contains(e.target)) {
        setPhotoPickerOpen(false);
      }
    };
    if (photoPickerOpen) document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [photoPickerOpen]);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: '' }));
  };

  useEffect(() => {
    const clean = compactReg(regSuffix);
    if (clean.length !== 4) {
      setRegSearch(null);
      setRegSearchBusy(false);
      return;
    }
    let cancelled = false;
    setRegSearchBusy(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${config.opsApiBase}/driver-registration/vehicles-by-reg-suffix?suffix=${encodeURIComponent(clean)}`
        );
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setRegSearch({ error: data.detail || 'Search failed' });
          return;
        }
        setRegSearch(data);
      } catch {
        if (!cancelled) setRegSearch({ error: 'Could not search vehicles' });
      } finally {
        if (!cancelled) setRegSearchBusy(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [regSuffix]);

  useEffect(() => {
    const query = form.vehicle.trim();
    if (query.length < 2 || form.vehicleClass != null) {
      setVehicleSuggestions([]);
      setVehicleSearchBusy(false);
      return;
    }

    let cancelled = false;
    setVehicleSearchBusy(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${config.opsApiBase}/driver-registration/vehicle-master-search?q=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        if (cancelled) return;
        setVehicleSuggestions(res.ok && data.success ? (data.vehicles || []) : []);
        setVehicleSuggestionsOpen(true);
      } catch {
        if (!cancelled) setVehicleSuggestions([]);
      } finally {
        if (!cancelled) setVehicleSearchBusy(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.vehicle, form.vehicleClass]);

  const handlePhotoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVehiclePhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setVehiclePhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
    setPhotoPickerOpen(false);
    // reset input so the same file can be re-selected
    e.target.value = '';
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditError('');
    setEditPhoto(null);
    setEditExt('');
    setEditForm({
      name: row.name || '',
      phoneNumber: row.phone_number || '',
      vehicle: row.vehicle_name || '',
      make: row.make || '',
      model: row.model || '',
      vehicleClass: row.vehicle_class != null ? String(row.vehicle_class) : '',
      locationText: row.address || '',
      locationData: row.base_lat != null && row.base_lng != null
        ? { lat: row.base_lat, lng: row.base_lng, address: row.address || '' }
        : null,
      comments: row.comments || '',
      bodyType: row.body_type || '',
      tonnage: row.tonnage != null ? String(row.tonnage) : '',
      bedLength: row.bed_length != null ? String(row.bed_length) : '',
      perKmPrice: row.per_km_price != null ? String(row.per_km_price) : '',
      readyForLongTrip: Boolean(row.ready_for_long_trip),
      longInterDistrict: Boolean(row.long_inter_district),
      longInterState: Boolean(row.long_inter_state),
      local: Boolean(row.local),
      intraStatePermit: Boolean(row.intra_state_permit),
      phoneExtensions: parsePhoneExtensions(row.phone_extensions),
      registrationNo: row.registration_no || '',
    });
  };

  const addEditExt = () => {
    const val = editExt.trim();
    const current = editForm?.phoneExtensions || [];
    if (!val || !/^\+?\d{8,15}$/.test(val) || current.includes(val)) return;
    setEdit('phoneExtensions', [...current, val]);
    setEditExt('');
  };

  const saveEdit = async () => {
    if (!editRow || !editForm) return;
    if (!editForm.name.trim() || editForm.name.trim().length < 2) {
      setEditError('Enter full name (min 2 characters)');
      return;
    }
    if (editForm.phoneNumber.replace(/\D/g, '').length < 10) {
      setEditError('Enter a valid 10-digit phone number');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const body = {
        name: editForm.name.trim(),
        phone_number: normalizePhone(editForm.phoneNumber.trim()),
        vehicle_name: editForm.vehicle.trim() || null,
        make: (editForm.make || '').trim() || null,
        model: (editForm.model || '').trim() || null,
        vehicle_class: editForm.vehicleClass !== '' ? Number(editForm.vehicleClass) : null,
        address: (editForm.locationData?.address || editForm.locationText || '').trim() || null,
        base_lat: editForm.locationData?.lat ?? null,
        base_lng: editForm.locationData?.lng ?? null,
        comments: editForm.comments.trim() || null,
        body_type: editForm.bodyType.trim() || null,
        tonnage: editForm.tonnage === '' ? null : Number(editForm.tonnage),
        bed_length: editForm.bedLength === '' ? null : Number(editForm.bedLength),
        per_km_price: editForm.perKmPrice === '' ? null : Number(editForm.perKmPrice),
        ready_for_long_trip: Boolean(editForm.readyForLongTrip),
        longInterDistrict: Boolean(editForm.longInterDistrict),
        long_inter_state: Boolean(editForm.longInterState),
        local: Boolean(editForm.local),
        intra_state_permit: Boolean(editForm.intraStatePermit),
        phone_extensions: editForm.phoneExtensions || [],
        registration_no: editForm.registrationNo.trim() || null,
      };
      const res = await fetch(`${config.opsApiBase}/driver-registration/${editRow.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to update driver');
      }
      if (editPhoto) {
        const compressed = await compressImage(editPhoto);
        const formData = new FormData();
        formData.append('file', compressed);
        const photoRes = await fetch(
          `${API}/api/v1/ops/driver-registration/${editRow.id}/vehicle-photo`,
          { method: 'POST', body: formData }
        );
        if (!photoRes.ok) {
          throw new Error('Details saved, but photo upload failed');
        }
      }
      setEditRow(null);
      setEditForm(null);
      setEditPhoto(null);
      setMsg({ type: 'green', text: 'Driver updated' });
      await fetchDrivers({ resetPage: false });
    } catch (err) {
      setEditError(err.message || 'Failed to update driver');
    } finally {
      setEditSaving(false);
    }
  };

  const removePhoto = () => {
    setVehiclePhoto(null);
    setVehiclePhotoPreview(null);
  };

  const validate = () => {
    const e = {};
    if (!form.driverName.trim() || form.driverName.trim().length < 2)
      e.driverName = 'Enter full name (min 2 characters)';
    if (form.phoneNumber.replace(/\D/g, '').length < 10)
      e.phoneNumber = 'Enter a valid 10-digit phone number';
    if (!form.locationData)
      e.locationText = 'Select a location from the dropdown';
    if (form.vehicle.trim() && form.vehicleClass == null)
      e.vehicle = 'Select a vehicle from the database suggestions';
    const fullReg = compactReg(form.registrationNo);
    if (fullReg.length < 6)
      e.registrationNo = 'Enter the full vehicle number';
    return e;
  };

  const uploadPhotoForRegistration = async (registrationId) => {
    if (!vehiclePhoto) return true;
    setPhotoUploading(true);
    try {
      const compressed = await compressImage(vehiclePhoto);
      const formData = new FormData();
      formData.append('file', compressed);
      const res = await fetch(
        `${API}/api/v1/ops/driver-registration/${registrationId}/vehicle-photo`,
        { method: 'POST', body: formData }
      );
      if (!res.ok) {
        throw new Error(`Server returned status ${res.status}`);
      }
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || 'Unknown error');
      }
      return true;
    } catch (err) {
      console.warn('Photo upload error:', err);
      return false;
    } finally {
      setPhotoUploading(false);
    }
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setBusy(true);
    setMsg(null);
    const phone = normalizePhone(form.phoneNumber.trim());

    try {
      const regRes = await fetch(`${API}/api/v1/ops/driver-registration/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.driverName.trim(),
          phone_number: phone,
          vehicle_name: form.vehicle.trim() || null,
          make: form.make.trim() || null,
          model: form.model.trim() || null,
          vehicle_class: form.vehicleClass,
          is_registered: true,
          base_lat: parseFloat(form.locationData.lat),
          base_lng: parseFloat(form.locationData.lng),
          address: form.locationData.address,
          comments: form.comments.trim() || null,
          registration_no: form.registrationNo.trim(),
          body_type: form.bodyType.trim() || null,
          tonnage: form.tonnage === '' ? null : Number(form.tonnage),
          bed_length: form.bedLength === '' ? null : Number(form.bedLength),
          per_km_price: form.perKmPrice === '' ? null : Number(form.perKmPrice),
          ready_for_long_trip: Boolean(form.readyForLongTrip),
          long_inter_district: Boolean(form.longInterDistrict),
          long_inter_state: Boolean(form.longInterState),
          local: Boolean(form.local),
          intra_state_permit: Boolean(form.intraStatePermit),
          phone_extensions: form.phoneExtensions,
        }),
      });
      const regData = await regRes.json();
      if (!regData.success) {
        setMsg({ type: 'red', text: regData.detail || regData.message || 'Failed to register driver.' });
        return;
      }

      // Upload vehicle photo if one was selected
      const registrationId = regData.record?.id;
      let photoSuccess = true;
      if (vehiclePhoto && registrationId) {
        photoSuccess = await uploadPhotoForRegistration(registrationId);
      }

      if (!photoSuccess) {
        setMsg({ type: 'red', text: `⚠️ Driver registered, but vehicle photo upload failed. Please try again.` });
        fetchDrivers();
        return;
      }

      setMsg({ type: 'green', text: `✅ Driver registered successfully with photo!` });
      setForm({
        driverName: '', phoneNumber: '', vehicle: '', make: '', model: '', locationText: '', locationData: null, comments: '',
        bodyType: '', tonnage: '', bedLength: '', perKmPrice: '',
        readyForLongTrip: false, longInterDistrict: false, longInterState: false, local: false, intraStatePermit: false,
        phoneExtensions: [], registrationNo: '', vehicleClass: null,
      });
      setNewPhoneExt('');
      setRegSuffix('');
      setRegSearch(null);
      setVehiclePhoto(null);
      setVehiclePhotoPreview(null);
      fetchDrivers();
    } catch {
      setMsg({ type: 'red', text: 'Network error — please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ops-register-page" style={S.page}>
      {/* Lightbox for full-screen photo preview */}
      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <div style={{ position: 'relative', maxWidth: '92vw', maxHeight: '92vh' }}>
            <img
              src={lightboxSrc}
              alt="Vehicle"
              style={{ maxWidth: '100%', maxHeight: '88vh', borderRadius: 12, objectFit: 'contain', display: 'block' }}
              onClick={e => e.stopPropagation()}
            />
            <button
              onClick={() => setLightboxSrc(null)}
              style={{
                position: 'absolute', top: -13, right: -13,
                background: '#ef4444', color: '#fff', border: 'none',
                borderRadius: '50%', width: 32, height: 32,
                fontSize: 18, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >×</button>
          </div>
        </div>
      )}

      {editRow && editForm && (
        <div
          className="ops-register-edit-backdrop"
          onClick={() => { if (!editSaving) { setEditRow(null); setEditForm(null); } }}
          style={{
            position: 'fixed', inset: 0, zIndex: 99990,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            overflowY: 'auto', padding: '24px 12px',
          }}
        >
          <div
            className="ops-register-edit-sheet"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 14,
              width: '100%',
              maxWidth: 640,
              padding: 22,
              boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f' }}>Edit driver #{editRow.id}</div>
              <button
                type="button"
                onClick={() => { if (!editSaving) { setEditRow(null); setEditForm(null); } }}
                style={{ border: 'none', background: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280' }}
              >
                ×
              </button>
            </div>
            {editError && <div style={S.alert('red')}>{editError}</div>}
            <div className="ops-register-grid-2">
              <div style={S.fg}>
                <label style={S.label}>Name *</label>
                <input style={S.input} value={editForm.name} onChange={(e) => setEdit('name', e.target.value)} />
              </div>
              <div style={S.fg}>
                <label style={S.label}>Phone *</label>
                <input style={S.input} value={editForm.phoneNumber} onChange={(e) => setEdit('phoneNumber', e.target.value)} />
              </div>
              <div style={S.fg}>
                <label style={S.label}>Make</label>
                <input
                  type="text"
                  name="make"
                  id="ops-edit-make"
                  autoComplete="on"
                  style={S.input}
                  value={editForm.make || ''}
                  onChange={(e) => {
                    const mk = e.target.value;
                    setEditForm(prev => ({
                      ...prev,
                      make: mk,
                      vehicle: [mk, prev.model].filter(Boolean).join(' ').trim(),
                    }));
                  }}
                  placeholder="e.g. Ashok Leyland"
                />
              </div>
              <div style={S.fg}>
                <label style={S.label}>Model</label>
                <input
                  type="text"
                  name="model"
                  id="ops-edit-model"
                  autoComplete="on"
                  style={S.input}
                  value={editForm.model || ''}
                  onChange={(e) => {
                    const mdl = e.target.value;
                    setEditForm(prev => ({
                      ...prev,
                      model: mdl,
                      vehicle: [prev.make, mdl].filter(Boolean).join(' ').trim(),
                    }));
                  }}
                  placeholder="e.g. 2525"
                />
              </div>
              <div style={S.fg}>
                <label style={S.label}>Reg No</label>
                <input style={S.input} value={editForm.registrationNo} onChange={(e) => setEdit('registrationNo', e.target.value)} placeholder="KA 19 AE 8030" />
              </div>
              <div style={S.fg}>
                <label style={S.label}>Vehicle class</label>
                <input style={S.input} type="number" value={editForm.vehicleClass} onChange={(e) => setEdit('vehicleClass', e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div style={S.fg}>
              <label style={S.label}>Body type</label>
              <select style={S.input} value={editForm.bodyType || ''} onChange={(e) => setEdit('bodyType', e.target.value)}>
                <option value="">Select body type</option>
                {editForm.bodyType && !['Open', 'Closed', 'Container'].includes(editForm.bodyType) && (
                  <option value={editForm.bodyType}>{editForm.bodyType}</option>
                )}
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
                <option value="Container">Container</option>
              </select>
            </div>
            <div className="ops-register-grid-2">
              <div style={S.fg}>
                <label style={S.label}>Tonnage (ton)</label>
                <input style={S.input} type="number" step="0.01" value={editForm.tonnage} onChange={(e) => setEdit('tonnage', e.target.value)} />
              </div>
              <div style={S.fg}>
                <label style={S.label}>Bed length</label>
                <input style={S.input} type="number" step="0.01" value={editForm.bedLength || ''} onChange={(e) => setEdit('bedLength', e.target.value)} placeholder="e.g. 7.5" />
              </div>
              <div style={S.fg}>
                <label style={S.label}>Per KM price (₹)</label>
                <input style={S.input} type="number" step="0.01" value={editForm.perKmPrice} onChange={(e) => setEdit('perKmPrice', e.target.value)} />
              </div>
            </div>
            <div style={S.fg}>
              <label style={S.label}>Base location</label>
              <PlacesAutocompleteInput
                value={editForm.locationText}
                onChange={(v) => setEdit('locationText', v)}
                onPlaceSelect={(place) => {
                  setEditForm((f) => f ? {
                    ...f,
                    locationText: place.address,
                    locationData: { lat: place.lat, lng: place.lng, address: place.address },
                  } : f);
                }}
                placeholder="Type to search — select from dropdown..."
                inputStyle={S.input}
              />
            </div>
            <div style={S.fg}>
              <label style={S.label}>Comments</label>
              <textarea
                style={{ ...S.input, minHeight: 72, resize: 'vertical' }}
                value={editForm.comments}
                onChange={(e) => setEdit('comments', e.target.value)}
              />
            </div>
            <div style={S.fg}>
              <label style={S.label}>Phone extensions</label>
              {(editForm.phoneExtensions || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {editForm.phoneExtensions.map((phone) => (
                    <span key={phone} style={{ fontSize: 12, background: '#f3f4f6', borderRadius: 6, padding: '3px 8px' }}>
                      {phone}
                      <button
                        type="button"
                        onClick={() => setEdit('phoneExtensions', editForm.phoneExtensions.filter((p) => p !== phone))}
                        style={{ marginLeft: 6, border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer' }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  style={S.input}
                  value={editExt}
                  onChange={(e) => setEditExt(e.target.value)}
                  placeholder="e.g. 919900000000"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEditExt(); } }}
                />
                <button type="button" onClick={addEditExt} style={{ padding: '11px 14px', border: 'none', background: '#6366f1', color: '#fff', borderRadius: 9, fontWeight: 600, cursor: 'pointer' }}>Add</button>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 16px', fontSize: 13, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={Boolean(editForm.intraStatePermit)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEditForm((f) => f ? {
                      ...f,
                      intraStatePermit: checked,
                      readyForLongTrip: checked ? true : f.readyForLongTrip,
                      longInterState: checked ? true : f.longInterState,
                    } : f);
                  }}
                />
                All India permit
              </label>
              {[
                ['readyForLongTrip', 'Ready for long trip'],
                ['longInterDistrict', 'Long intra-district'],
                ['longInterState', 'Long intra-state'],
                ['local', 'Local'],
              ].map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={Boolean(editForm[key])} onChange={(e) => setEdit(key, e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>
            <div style={S.fg}>
              <label style={S.label}>Replace vehicle photo</label>
              <input
                ref={editPhotoInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  setEditPhoto(file || null);
                  e.target.value = '';
                }}
              />
              {editPhoto && <div style={S.hint}>{editPhoto.name}</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                onClick={() => { setEditRow(null); setEditForm(null); }}
                disabled={editSaving}
                style={{ padding: '10px 16px', border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={editSaving}
                style={{ padding: '10px 16px', border: 'none', background: editSaving ? '#93c5fd' : '#2563eb', color: '#fff', borderRadius: 8, fontWeight: 700, cursor: editSaving ? 'not-allowed' : 'pointer' }}
              >
                {editSaving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="ops-register-banner" style={S.header}>
        <p style={S.headerTitle}>Register Driver</p>
        <p style={S.headerSub}>OPS field team — enter details to onboard a new driver</p>
      </div>

      {/* Form */}
      <div className="ops-register-body" style={S.body}>
        <div className="ops-register-card" style={S.card}>

          {msg && <div style={S.alert(msg.type)}>{msg.text}</div>}

          {/* Last-4 RC lookup — live, no search button */}
          <div style={S.fg}>
            <label style={S.label}>Last 4 of RC <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional check)</span></label>
            <input
              style={S.input}
              placeholder="8030"
              maxLength={4}
              value={regSuffix}
              onChange={e => {
                setRegSuffix(compactReg(e.target.value).slice(0, 4));
              }}
            />
            {regSuffix.length < 4 && <div style={S.hint}>Optional — type last 4 to see if this vehicle is already onboarded</div>}
            {regSearchBusy && <div style={S.hint}>Checking…</div>}
            {regSearch?.error && <div style={S.err}>{regSearch.error}</div>}
            {regSearch && !regSearch.error && !regSearch.onboarded && (
              <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: '#166534' }}>
                Not onboarded
              </div>
            )}
            {regSearch?.onboarded && (
              <div style={{
                marginTop: 8,
                padding: '8px 10px',
                borderRadius: 8,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#991b1b',
                fontSize: 13,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Already onboarded</div>
                {regSearch.vehicles.map(v => (
                  <div key={v.vehicle_id} style={{ marginTop: 4 }}>
                    Vehicle #{v.vehicle_id} · {v.registration_no || '—'}
                    {v.driver_name ? ` · ${v.driver_name}` : ''}
                    {v.driver_phone ? ` · ${v.driver_phone}` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.fg}>
            <label style={S.label}>Full vehicle number *</label>
            <input
              style={errors.registrationNo ? S.inputErr : S.input}
              placeholder="KA 19 AE 8030"
              value={form.registrationNo}
              onChange={e => set('registrationNo', e.target.value.toUpperCase())}
            />
            {errors.registrationNo && <div style={S.err}>{errors.registrationNo}</div>}
            <div style={S.hint}>Enter the complete registration number, not just the last 4</div>
          </div>

          {/* Driver Name */}
          <div style={S.fg}>
            <label style={S.label}>Driver Name *</label>
            <input
              style={errors.driverName ? S.inputErr : S.input}
              placeholder="Full name"
              value={form.driverName}
              onChange={e => set('driverName', e.target.value)}
            />
            {errors.driverName && <div style={S.err}>{errors.driverName}</div>}
          </div>

          {/* Phone */}
          <div style={S.fg}>
            <label style={S.label}>Phone Number *</label>
            <input
              style={errors.phoneNumber ? S.inputErr : S.input}
              placeholder="9876543210"
              type="tel"
              value={form.phoneNumber}
              onChange={e => set('phoneNumber', e.target.value)}
            />
            {errors.phoneNumber && <div style={S.err}>{errors.phoneNumber}</div>}
          </div>

          {/* Vehicle (optional) */}
          <div style={S.fg}>
            <label style={S.label}>Vehicle <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
            <div style={{ position: 'relative' }}>
              <input
                style={errors.vehicle ? S.inputErr : S.input}
                placeholder="Type at least 2 letters, e.g. Ta"
                value={form.vehicle}
                autoComplete="off"
                onFocus={() => {
                  if (vehicleSuggestions.length) setVehicleSuggestionsOpen(true);
                }}
                onBlur={() => setTimeout(() => setVehicleSuggestionsOpen(false), 150)}
                onChange={e => {
                  setForm(f => ({ ...f, vehicle: e.target.value, vehicleClass: null }));
                  setErrors(current => ({ ...current, vehicle: '' }));
                }}
              />
              {vehicleSuggestionsOpen && vehicleSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  zIndex: 200,
                  maxHeight: 260,
                  overflowY: 'auto',
                  background: '#fff',
                  border: '1px solid #d1d5db',
                  borderRadius: 9,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }}>
                  {vehicleSuggestions.map((vehicle) => (
                    <button
                      key={`${vehicle.vehicle_name}-${vehicle.vehicle_class}`}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setForm(f => ({
                          ...f,
                          vehicle: vehicle.vehicle_name,
                          make: vehicle.make || '',
                          model: vehicle.model || '',
                          vehicleClass: vehicle.vehicle_class,
                          tonnage: vehicle.tonnage != null ? String(vehicle.tonnage) : f.tonnage,
                          bedLength: vehicle.bed_length != null ? String(vehicle.bed_length) : f.bedLength,
                        }));
                        setErrors(current => ({ ...current, vehicle: '' }));
                        setVehicleSuggestionsOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '11px 13px',
                        border: 'none',
                        borderBottom: '1px solid #f3f4f6',
                        background: '#fff',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{vehicle.vehicle_name}</span>
                      <span style={{ color: '#64748b', marginLeft: 12 }}>
                        Class {vehicle.vehicle_class}
                        {vehicle.tonnage != null ? ` · ${vehicle.tonnage}T` : ''}
                        {vehicle.bed_length != null ? ` · ${vehicle.bed_length}ft` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {errors.vehicle && <div style={S.err}>{errors.vehicle}</div>}
            {vehicleSearchBusy && <div style={S.hint}>Searching vehicle database…</div>}
            {!vehicleSearchBusy && form.vehicleClass != null && (
              <div style={{ ...S.hint, color: '#16a34a' }}>
                Selected from database · Class {form.vehicleClass}
              </div>
            )}
            {!vehicleSearchBusy && form.vehicleClass == null && !errors.vehicle && (
              <div style={S.hint}>Select a database result, or leave blank if unknown</div>
            )}

            {/* Separate Make & Model fields */}
            {(form.make || form.model || form.vehicleClass != null) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px' }}>
                <div>
                  <label style={{ ...S.label, fontSize: '12px' }}>Make</label>
                  <input
                    type="text"
                    name="make"
                    id="reg-make"
                    autoComplete="on"
                    style={S.input}
                    placeholder="e.g. Ashok Leyland"
                    value={form.make}
                    onChange={e => {
                      const m = e.target.value;
                      setForm(f => ({
                        ...f,
                        make: m,
                        vehicle: [m, f.model].filter(Boolean).join(' ').trim(),
                      }));
                    }}
                  />
                </div>
                <div>
                  <label style={{ ...S.label, fontSize: '12px' }}>Model</label>
                  <input
                    type="text"
                    name="model"
                    id="reg-model"
                    autoComplete="on"
                    style={S.input}
                    placeholder="e.g. 2525"
                    value={form.model}
                    onChange={e => {
                      const mo = e.target.value;
                      setForm(f => ({
                        ...f,
                        model: mo,
                        vehicle: [f.make, mo].filter(Boolean).join(' ').trim(),
                      }));
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Stand / Base Location */}
          <div style={S.fg}>
            <label style={S.label}>Stand / Base Location *</label>
            <div style={{
              border: errors.locationText ? '1px solid #ef4444' : '1px solid #d1d5db',
              borderRadius: 9,
              background: '#fff',
            }}>
              <PlacesAutocompleteInput
                value={form.locationText}
                onChange={v => {
                  set('locationText', v);
                  if (!v) set('locationData', null);
                }}
                onPlaceSelect={({ address, lat, lng }) => {
                  setForm(f => ({ ...f, locationText: address, locationData: { address, lat, lng } }));
                  setErrors(e => ({ ...e, locationText: '' }));
                }}
                placeholder="Type to search — select from dropdown..."
                inputStyle={{ border: 'none', outline: 'none', padding: '11px 13px', fontSize: 16, width: '100%' }}
              />
            </div>
            {errors.locationText && <div style={S.err}>{errors.locationText}</div>}
            {form.locationData && (
              <div style={{ fontSize: 12, color: '#16a34a', marginTop: 5 }}>
                📍 {form.locationData.address}
              </div>
            )}
          </div>

          {/* Comments */}
          <div style={S.fg}>
            <label style={S.label}>Comments <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
            <textarea
              style={{
                ...S.input,
                resize: 'vertical',
                minHeight: 72,
                lineHeight: 1.5,
                fontFamily: 'inherit',
              }}
              placeholder="Any notes about this driver, reference, or context…"
              value={form.comments}
              onChange={e => set('comments', e.target.value)}
            />
          </div>

          {/* ── Vehicle specs ─────────────────────────────────────── */}
          <div style={{
            ...S.fg,
            padding: 14,
            border: '1px solid #bfdbfe',
            borderRadius: 12,
            background: '#f8fbff',
          }}>
            <div style={{ ...S.label, marginBottom: 12, fontSize: 14 }}>Vehicle specs <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional — copied on onboard)</span></div>
            <div style={S.fg}>
              <label style={S.label}>Body type</label>
              <select
                style={S.input}
                value={form.bodyType || ''}
                onChange={e => set('bodyType', e.target.value)}
              >
                <option value="">Select body type</option>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
                <option value="Container">Container</option>
              </select>
            </div>
            <div className="ops-register-grid-2">
              <div style={S.fg}>
                <label style={S.label}>Tonnage (ton) <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                <input
                  style={S.input}
                  type="number"
                  step="0.01"
                  placeholder="e.g. 1.5"
                  value={form.tonnage || ''}
                  onChange={e => set('tonnage', e.target.value)}
                />
              </div>
              <div style={S.fg}>
                <label style={S.label}>Bed length <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                <input
                  style={S.input}
                  type="number"
                  step="0.01"
                  placeholder="e.g. 7.5"
                  value={form.bedLength || ''}
                  onChange={e => set('bedLength', e.target.value)}
                />
              </div>
              <div style={S.fg}>
                <label style={S.label}>Per KM price (₹) <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                <input
                  style={S.input}
                  type="number"
                  step="0.01"
                  placeholder="e.g. 15.50"
                  value={form.perKmPrice || ''}
                  onChange={e => set('perKmPrice', e.target.value)}
                />
              </div>
            </div>
            <div style={S.fg}>
              <label style={S.label}>Phone extensions <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
              {(form.phoneExtensions || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {(form.phoneExtensions || []).map((phone) => (
                    <span key={phone} style={{ fontSize: 12, background: '#f3f4f6', borderRadius: 6, padding: '3px 8px' }}>
                      {phone}
                      <button
                        type="button"
                        onClick={() => set('phoneExtensions', (form.phoneExtensions || []).filter((p) => p !== phone))}
                        style={{ marginLeft: 6, border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer' }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  style={S.input}
                  placeholder="e.g. 919900000000"
                  value={newPhoneExt}
                  onChange={(e) => setNewPhoneExt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    const val = newPhoneExt.trim();
                    const current = form.phoneExtensions || [];
                    if (!val || !/^\+?\d{8,15}$/.test(val) || current.includes(val)) return;
                    set('phoneExtensions', [...current, val]);
                    setNewPhoneExt('');
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const val = newPhoneExt.trim();
                    const current = form.phoneExtensions || [];
                    if (!val || !/^\+?\d{8,15}$/.test(val) || current.includes(val)) return;
                    set('phoneExtensions', [...current, val]);
                    setNewPhoneExt('');
                  }}
                  style={{ padding: '11px 14px', border: 'none', background: '#6366f1', color: '#fff', borderRadius: 9, fontWeight: 600, cursor: 'pointer' }}
                >
                  Add
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 16px', fontSize: 13 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={Boolean(form.intraStatePermit)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((f) => ({
                      ...f,
                      intraStatePermit: checked,
                      readyForLongTrip: checked ? true : f.readyForLongTrip,
                      longInterState: checked ? true : f.longInterState,
                    }));
                  }}
                />
                All India permit
              </label>
              {[
                ['readyForLongTrip', 'Ready for long trip'],
                ['longInterDistrict', 'Long intra-district'],
                ['longInterState', 'Long intra-state'],
                ['local', 'Local'],
              ].map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form[key])}
                    onChange={(e) => set(key, e.target.checked)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* ── Vehicle Photo ─────────────────────────────────────── */}
          <div style={S.fg}>
            <label style={S.label}>
              Vehicle Photo{' '}
              <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
            </label>

            {vehiclePhotoPreview ? (
              /* Preview thumbnail with remove button */
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img
                  src={vehiclePhotoPreview}
                  alt="Vehicle preview"
                  onClick={() => setLightboxSrc(vehiclePhotoPreview)}
                  style={{
                    width: 72, height: 72, borderRadius: 10,
                    objectFit: 'cover', border: '2px solid #10b981',
                    cursor: 'zoom-in', boxShadow: '0 2px 8px rgba(16,185,129,0.25)',
                  }}
                  title="Click to view full image"
                />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#065f46', marginBottom: 4 }}>
                    ✅ Photo selected
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                    {vehiclePhoto?.name}
                  </div>
                  <button
                    type="button"
                    onClick={removePhoto}
                    style={{
                      fontSize: 12, padding: '4px 10px',
                      border: '1px solid #fca5a5', borderRadius: 6,
                      background: '#fff1f1', color: '#dc2626', cursor: 'pointer',
                    }}
                  >
                    ✕ Remove
                  </button>
                </div>
              </div>
            ) : (
              /* Add photo button + picker */
              <div style={{ position: 'relative', display: 'inline-block' }} ref={photoPickerRef}>
                {/* Hidden inputs */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={handlePhotoFile}
                />
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoFile}
                />

                {/* Trigger button */}
                <button
                  type="button"
                  onClick={() => setPhotoPickerOpen(v => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 16px',
                    border: '2px dashed #d1d5db',
                    borderRadius: 10,
                    background: '#f9fafb',
                    color: '#374151',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, background 0.2s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = '#10b981';
                    e.currentTarget.style.background = '#f0fdf4';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.background = '#f9fafb';
                  }}
                >
                  <span style={{ fontSize: 20 }}>📷</span>
                  Add Vehicle Photo
                </button>

                {/* Popup picker */}
                {photoPickerOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0,
                    background: '#fff',
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    zIndex: 100,
                    minWidth: 200,
                  }}>
                    <button
                      type="button"
                      onClick={() => { setPhotoPickerOpen(false); cameraInputRef.current?.click(); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        width: '100%', padding: '13px 16px',
                        border: 'none', background: 'none',
                        fontSize: 14, fontWeight: 600, color: '#111',
                        cursor: 'pointer', textAlign: 'left',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      <span style={{ fontSize: 20 }}>📸</span>
                      Open Camera
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPhotoPickerOpen(false); galleryInputRef.current?.click(); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        width: '100%', padding: '13px 16px',
                        border: 'none', background: 'none',
                        fontSize: 14, fontWeight: 600, color: '#111',
                        cursor: 'pointer', textAlign: 'left',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      <span style={{ fontSize: 20 }}>🖼️</span>
                      Upload from Gallery
                    </button>
                  </div>
                )}
              </div>
            )}
            <div style={S.hint}>
              Take a photo of the vehicle on site or choose from gallery — stored securely
            </div>
          </div>

          {/* Info box */}
          <div style={S.infoBox}>
            <strong>ℹ️ What happens next:</strong> This only saves the driver in Registered Drivers.
            No WhatsApp is sent from this page. To send Terms &amp; Conditions, open
            Vehicles → Register Vehicle and tap <em>Send Template</em>. They become active after
            you click <em>Onboard</em> (or after they complete WhatsApp onboarding).
          </div>

          {/* Submit */}
          <button
            style={busy ? S.btnDisabled : S.btn}
            onClick={submit}
            disabled={busy}
          >
            {busy
              ? (photoUploading ? 'Uploading photo…' : 'Registering…')
              : '🚛 Register Driver'}
          </button>

        </div>

        {/* ── Registered Drivers list ─────────────────────────────── */}
        <div className="ops-register-card" style={S.tableCard}>
          <div className="ops-register-table-title" style={S.tableTitle}>
            <span>👥 Registered Drivers</span>
            <button style={S.refreshBtn} onClick={fetchDrivers}>⟳ Refresh</button>
          </div>

          {driversLoading ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>Loading…</p>
          ) : drivers.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>No drivers registered yet.</p>
          ) : (
            <>
            <div className="ops-register-table-wrap">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Photo', 'ID', 'Name', 'Phone', 'Reg No', 'Vehicle', 'Body type', 'Base Location', 'Comments', 'Step', 'Action'].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const totalItems = drivers.length;
                    const totalPages = Math.ceil(totalItems / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedDrivers = drivers.slice(startIndex, endIndex);

                    return (
                      <>
                        {paginatedDrivers.map(row => (
                          <tr key={row.id}>
                            {/* Vehicle Photo thumbnail */}
                            <td style={{ ...S.td, width: 46 }}>
                              {row.vehicle_photo_url ? (
                                <img
                                  src={row.vehicle_photo_url}
                                  alt="Vehicle"
                                  onClick={() => setLightboxSrc(row.vehicle_photo_url)}
                                  style={{
                                    width: 36, height: 36,
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '2px solid #10b981',
                                    cursor: 'zoom-in',
                                    display: 'block',
                                    boxShadow: '0 1px 4px rgba(16,185,129,0.3)',
                                  }}
                                  title="Click to view full image"
                                />
                              ) : (
                                <div style={{
                                  width: 36, height: 36, borderRadius: '50%',
                                  background: '#f3f4f6', border: '1.5px dashed #d1d5db',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 16, color: '#9ca3af',
                                }} title="No photo">🚛</div>
                              )}
                            </td>
                            <td style={S.td}>{row.id}</td>
                            <td style={S.td}>{row.name || '—'}</td>
                            <td style={S.td}>{row.phone_number}</td>
                            <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.registration_no || '—'}</td>
                            <td style={S.td}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                  {row.vehicle_name || [row.make, row.model].filter(Boolean).join(' ') || '—'}
                                </span>
                                {(() => {
                                  const { ton, len } = getRegTonAndLength(row);
                                  if (!ton && !len) return null;
                                  return (
                                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                                      {ton && (
                                        <span>Ton: <strong style={{ color: '#334155' }}>{ton}{String(ton).toLowerCase().includes('ton') || String(ton).toLowerCase().includes('t') || String(ton).toLowerCase().includes('kg') ? '' : ' Ton'}</strong></span>
                                      )}
                                      {ton && len && ' · '}
                                      {len && (
                                        <span>Length: <strong style={{ color: '#334155' }}>{len}{String(len).toLowerCase().includes('ft') || String(len).toLowerCase().includes('m') ? '' : ' ft'}</strong></span>
                                      )}
                                    </span>
                                  );
                                })()}
                              </div>
                            </td>
                            <td style={S.td}>{row.body_type || '—'}</td>
                            <td style={{ ...S.td, maxWidth: 160, whiteSpace: 'normal', fontSize: 12 }}>
                              {row.address ? (row.address.length > 50 ? row.address.slice(0, 50) + '…' : row.address) : '—'}
                            </td>
                            <td style={{ ...S.td, maxWidth: 180, whiteSpace: 'normal', fontSize: 12, color: row.comments ? '#1e3a5f' : '#9ca3af' }}>
                              {row.comments || '—'}
                            </td>
                            <td style={S.td}>
                              {row.current_step
                                ? <span style={S.stepBadge(row.current_step)}>{row.current_step}</span>
                                : '—'}
                            </td>
                            <td style={S.td}>
                              <button
                                type="button"
                                onClick={() => openEdit(row)}
                                style={{
                                  padding: '6px 12px',
                                  border: 'none',
                                  background: '#0ea5e9',
                                  color: '#fff',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>

            {(() => {
            const totalItems = drivers.length;
            const totalPages = Math.ceil(totalItems / itemsPerPage);
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;

            return totalPages > 1 ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 18px 12px',
                borderTop: '1px solid #f3f4f6',
                flexWrap: 'wrap',
                gap: 12
              }}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>
                  Showing <strong>{startIndex + 1}</strong> to <strong>{Math.min(endIndex, totalItems)}</strong> of <strong>{totalItems}</strong> entries
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      padding: '6px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      background: currentPage === 1 ? '#f9fafb' : '#fff',
                      color: currentPage === 1 ? '#9ca3af' : '#374151',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      padding: '6px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      background: currentPage === totalPages ? '#f9fafb' : '#fff',
                      color: currentPage === totalPages ? '#9ca3af' : '#374151',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null;
          })()}
            </>
          )}
        </div>

      </div>
    </div>
  );
}
